import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { useMobile } from '../hooks/useMobile';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';
import ProjectImportExport from './ProjectImportExport';
import SettingsModal from './SettingsModal';
import AIAppCreator from './AIAppCreator';
import AuthModal from './AuthModal';
import UserMenu from './UserMenu';

const { FiCode, FiPlus, FiFolder, FiTrash2, FiEdit3, FiSettings, FiUpload, FiEdit2, FiCopy, FiLogIn, FiCloud, FiWifi, FiUser, FiLoader, FiRefreshCw } = FiIcons;
const { BiBot } = BiIcons;

const StartScreen = () => {
  const navigate = useNavigate();
  const { user, authInitialized, loading: authLoading } = useAuth();
  const { isMobile, isTablet } = useMobile();
  const { 
    projects, 
    createProject, 
    loadProject, 
    deleteProject, 
    renameProject, 
    duplicateProject, 
    syncing, 
    loading: projectsLoading,
    projectsLoaded 
  } = useProject();

  const [showNewProject, setShowNewProject] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showAICreator, setShowAICreator] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [projectName, setProjectName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('blank');
  const [renamingProject, setRenamingProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  const templates = [
    { id: 'blank', name: 'Blank Project', description: 'Start with a basic HTML file' },
    { id: 'vanilla', name: 'Vanilla JS', description: 'HTML, CSS, and JavaScript' },
    { id: 'react', name: 'React', description: 'React app with CDN' }
  ];

  const handleCreateProject = async () => {
    if (!projectName.trim() || creatingProject) return;

    try {
      setCreatingProject(true);
      console.log('🚀 Creating project:', projectName.trim(), 'with template:', selectedTemplate);
      
      const newProject = await createProject(projectName.trim(), selectedTemplate);
      console.log('✅ Project created, navigating to editor...');
      
      setShowNewProject(false);
      setProjectName('');
      setSelectedTemplate('blank');
      navigate('/editor');
    } catch (error) {
      console.error('❌ Error creating project:', error);
      alert('Failed to create project. Please try again.');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleLoadProject = (project) => {
    loadProject(project);
    navigate('/editor');
  };

  const handleDuplicateProject = async (e, project) => {
    e.stopPropagation();
    try {
      await duplicateProject(project);
    } catch (error) {
      console.error('❌ Error duplicating project:', error);
      alert('Failed to duplicate project. Please try again.');
    }
  };

  const handleRenameProject = async (projectId, newName) => {
    if (!newName.trim()) return;

    try {
      await renameProject(projectId, newName.trim());
      setRenamingProject(null);
      setNewProjectName('');
    } catch (error) {
      console.error('❌ Error renaming project:', error);
      alert('Failed to rename project. Please try again.');
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this project?')) return;

    try {
      await deleteProject(projectId);
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  const startRenaming = (project) => {
    setRenamingProject(project.id);
    setNewProjectName(project.name);
  };

  const openAuth = (mode = 'signin') => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  // Show loading while auth is initializing
  if (!authInitialized) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="text-6xl text-orange-400 animate-spin mb-4" />
          <p className="text-gray-400 text-lg">Initializing Angry Coder...</p>
        </div>
      </div>
    );
  }

  // Determine if we should show loading for projects
  const shouldShowProjectsLoading = user && !projectsLoaded && projectsLoading;

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      {/* SEO Content for Search Engines */}
      <div className="sr-only">
        <h1>Angry Coder - Professional Code Editor and Development Platform</h1>
        <p>Build amazing web applications with Angry Coder's powerful serverless code editor. Features include AI-powered coding assistance, live preview, project management, and support for HTML, CSS, JavaScript, React, and more.</p>
        <p>Perfect for developers, students, and professionals who want to code anywhere, anytime without setup.</p>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Header with Logo and Auth - Mobile Optimized */}
        <motion.div
          className={`flex items-center justify-between mb-8 md:mb-16 ${isMobile ? 'flex-col gap-4' : ''}`}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className={`flex items-center ${isMobile ? 'flex-col text-center' : ''}`}>
            {/* Logo */}
            <div className={isMobile ? 'mb-4' : 'mr-8'}>
              <img
                src="https://res.cloudinary.com/frontly/image/upload/v1756676497/Angry_Branding_Logo_ykea75.png"
                alt="Angry Coder Logo"
                className={`object-contain ${isMobile ? 'w-16 h-16' : 'w-24 h-24'}`}
                loading="eager"
              />
            </div>

            {/* Brand Text */}
            <div className={isMobile ? 'text-center' : 'text-left'}>
              <h1 className={`font-bold bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 bg-clip-text text-transparent mb-2 ${isMobile ? 'text-3xl' : 'text-6xl'}`}>
                Angry Coder
              </h1>
              <p className={`text-gray-400 font-light ${isMobile ? 'text-lg' : 'text-xl'}`}>
                Professional serverless development platform
              </p>
              <p className={`text-gray-500 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                Code with fury, build with passion 🔥
              </p>
            </div>
          </div>

          {/* Auth Section - Mobile Optimized */}
          <div className={`flex items-center gap-4 ${isMobile ? 'w-full justify-center' : ''}`}>
            {/* Sync Status */}
            {user && (
              <div className="flex items-center text-sm text-gray-400">
                <SafeIcon 
                  icon={syncing || authLoading ? FiWifi : FiCloud} 
                  className={`mr-1 ${syncing || authLoading ? 'animate-pulse text-orange-400' : 'text-green-400'}`} 
                />
                {syncing ? 'Syncing...' : authLoading ? 'Loading...' : 'Synced'}
              </div>
            )}

            {user ? (
              <UserMenu onOpenSettings={() => setShowSettings(true)} />
            ) : (
              <div className={`flex gap-3 ${isMobile ? 'flex-col w-full max-w-xs' : ''}`}>
                <button
                  onClick={() => openAuth('signin')}
                  disabled={authLoading}
                  className={`flex items-center justify-center px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800 disabled:opacity-50 rounded-lg transition-colors duration-200 ${isMobile ? 'w-full' : ''}`}
                >
                  <SafeIcon icon={FiLogIn} className="mr-2" />
                  Sign In
                </button>
                <button
                  onClick={() => openAuth('signup')}
                  disabled={authLoading}
                  className={`flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:from-gray-700 disabled:to-gray-800 disabled:opacity-50 rounded-lg transition-colors duration-200 ${isMobile ? 'w-full' : ''}`}
                >
                  <SafeIcon icon={FiUser} className="mr-2" />
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.div
          className={`text-center max-w-3xl mx-auto mb-8 md:mb-16 ${isMobile ? 'px-4' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <p className={`text-gray-300 leading-relaxed ${isMobile ? 'text-base' : 'text-lg'}`}>
            Unleash your coding potential with our powerful browser-based IDE. Build web applications with{' '}
            <span className="text-orange-400 font-semibold">AI assistance</span>, live preview, and professional development tools.
          </p>
          {!user && (
            <p className={`text-gray-500 mt-4 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              <SafeIcon icon={FiCloud} className="inline mr-1" />
              Sign up to save your projects in the cloud and access them from anywhere!
            </p>
          )}
        </motion.div>

        {/* Action Buttons - Mobile Optimized */}
        <motion.div
          className={`flex justify-center gap-4 md:gap-6 mb-8 md:mb-16 ${isMobile ? 'flex-col max-w-sm mx-auto' : 'flex-wrap'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <button
            onClick={() => setShowNewProject(true)}
            className={`flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-red-500/25 ${isMobile ? 'w-full' : ''}`}
          >
            <SafeIcon icon={FiPlus} className={`mr-3 ${isMobile ? 'text-lg' : 'text-xl'}`} />
            <span className={`font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>New Project</span>
          </button>

          <button
            onClick={() => setShowAICreator(true)}
            className={`flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25 ${isMobile ? 'w-full' : ''}`}
          >
            <SafeIcon icon={BiBot} className={`mr-3 ${isMobile ? 'text-lg' : 'text-xl'}`} />
            <span className={`font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>Create with AI</span>
          </button>

          <button
            onClick={() => setShowImportExport(true)}
            className={`flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-emerald-500/25 ${isMobile ? 'w-full' : ''}`}
          >
            <SafeIcon icon={FiUpload} className={`mr-3 ${isMobile ? 'text-lg' : 'text-xl'}`} />
            <span className={`font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>Import/Export</span>
          </button>

          <button
            onClick={() => setShowSettings(true)}
            className={`flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg ${isMobile ? 'w-full' : ''}`}
          >
            <SafeIcon icon={FiSettings} className={`mr-3 ${isMobile ? 'text-lg' : 'text-xl'}`} />
            <span className={`font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>Settings</span>
          </button>
        </motion.div>

        {/* Features Section - Mobile Optimized */}
        <motion.div
          className={`mb-8 md:mb-16 grid grid-cols-1 ${
            isTablet ? 'md:grid-cols-2 lg:grid-cols-3' : isMobile ? '' : 'md:grid-cols-3'
          } gap-4 md:gap-6`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={BiBot} className="text-2xl text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">AI-Powered Coding</h3>
            <p className="text-gray-400 text-sm">Generate apps and get coding help with advanced AI assistance</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/30 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCode} className="text-2xl text-orange-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Live Preview</h3>
            <p className="text-gray-400 text-sm">See your changes instantly with real-time preview and debugging</p>
          </div>

          <div className={`bg-gradient-to-br from-yellow-500/10 to-red-500/10 border border-yellow-500/30 rounded-xl p-6 text-center ${isMobile && !isTablet ? 'md:col-span-1' : ''}`}>
            <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={user ? FiCloud : FiFolder} className="text-2xl text-yellow-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {user ? 'Cloud Storage' : 'Project Management'}
            </h3>
            <p className="text-gray-400 text-sm">
              {user 
                ? 'Your projects are automatically saved and synced across all devices' 
                : 'Organize, import, export, and manage all your projects effortlessly'
              }
            </p>
          </div>
        </motion.div>

        {/* Recent Projects */}
        <motion.div
          className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-4 md:p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className={`flex items-center justify-between mb-6 md:mb-8 ${isMobile ? 'flex-col gap-4' : ''}`}>
            <h2 className={`font-bold flex items-center text-white ${isMobile ? 'text-2xl text-center' : 'text-3xl'}`}>
              <SafeIcon icon={FiFolder} className={`mr-4 text-orange-400 ${isMobile ? 'text-2xl' : ''}`} />
              Your Projects
              {user && (
                <div className={`ml-4 flex items-center text-sm text-green-400 ${isMobile ? 'text-xs' : ''}`}>
                  <SafeIcon icon={FiCloud} className="mr-1" />
                  Cloud Synced
                </div>
              )}
            </h2>

            <div className={`flex items-center gap-3 ${isMobile ? 'w-full justify-center' : ''}`}>
              {!user && projects.length > 0 && (
                <button
                  onClick={() => openAuth('signup')}
                  className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors duration-200 text-sm"
                >
                  <SafeIcon icon={FiCloud} className="mr-2" />
                  Save to Cloud
                </button>
              )}
            </div>
          </div>

          {shouldShowProjectsLoading && (
            <div className="text-center py-8">
              <SafeIcon icon={FiLoader} className="text-4xl text-orange-400 animate-spin mb-4" />
              <p className="text-gray-400">Loading your projects...</p>
              <p className="text-gray-500 text-sm mt-2">This should only take a moment...</p>
            </div>
          )}

          {!shouldShowProjectsLoading && projects.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800 inline-block mb-6">
                <SafeIcon icon={FiCode} className="text-6xl text-gray-600" />
              </div>
              <p className="text-xl mb-2">Ready to get angry with code? 😤</p>
              <p className="text-gray-600 mb-4">Create your first project and start building something amazing!</p>
              {!user && (
                <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg inline-block">
                  <p className="text-orange-300 text-sm mb-2">💡 Pro Tip:</p>
                  <p className="text-orange-200 text-sm">Sign up to save your projects in the cloud!</p>
                </div>
              )}
            </div>
          ) : !shouldShowProjectsLoading && (
            <div className={`grid grid-cols-1 gap-4 md:gap-6 ${
              isTablet ? 'md:grid-cols-2 lg:grid-cols-3' : isMobile ? '' : 'md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-4 md:p-6 hover:border-orange-500/50 transition-all duration-300 cursor-pointer group transform hover:scale-[1.02]"
                  whileHover={{ y: -4 }}
                >
                  <div className="flex justify-between items-start mb-4">
                    {renamingProject === project.id ? (
                      <input
                        type="text"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        onBlur={() => handleRenameProject(project.id, newProjectName)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleRenameProject(project.id, newProjectName);
                          } else if (e.key === 'Escape') {
                            setRenamingProject(null);
                            setNewProjectName('');
                          }
                        }}
                        className="bg-gray-700 text-white px-2 py-1 rounded flex-1 mr-2 text-lg font-bold"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <h3
                        className="font-bold text-lg text-white truncate cursor-pointer"
                        onClick={() => handleLoadProject(project)}
                      >
                        {project.name}
                      </h3>
                    )}

                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => handleDuplicateProject(e, project)}
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/10 p-2 rounded-lg transition-all duration-200"
                        title="Duplicate Project"
                      >
                        <SafeIcon icon={FiCopy} className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startRenaming(project);
                        }}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-2 rounded-lg transition-all duration-200"
                        title="Rename Project"
                      >
                        <SafeIcon icon={FiEdit2} className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteProject(e, project.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2 rounded-lg transition-all duration-200"
                        title="Delete Project"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    </div>
                  </div>

                  <div
                    className="space-y-3"
                    onClick={() => renamingProject !== project.id && handleLoadProject(project)}
                  >
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <div className="flex items-center">
                        <span className="font-medium">Template:</span>
                        <span className="ml-2 px-2 py-1 bg-orange-500/20 text-orange-400 rounded-md">
                          {project.template}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs">
                          {Object.keys(project.files || {}).length} files
                        </span>
                        {user && (
                          <SafeIcon icon={FiCloud} className="text-green-400" title="Synced to cloud" />
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-gray-500">
                      Created: {new Date(project.createdAt).toLocaleDateString()}
                    </p>

                    <div className="flex items-center pt-2 text-orange-400 border-t border-gray-700">
                      <SafeIcon icon={FiEdit3} className="mr-2" />
                      <span className="text-sm font-medium">Click to open</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* New Project Modal */}
      {showNewProject && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            className={`bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-6 md:p-8 w-full shadow-2xl ${isMobile ? 'max-w-sm' : 'max-w-lg'}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className={`font-bold mb-6 text-white ${isMobile ? 'text-xl' : 'text-2xl'}`}>Create New Project</h3>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3 text-gray-300">Project Name</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none text-white"
                placeholder="My Awesome Project"
                disabled={creatingProject}
              />
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold mb-3 text-gray-300">Template</label>
              <div className="space-y-3">
                {templates.map((template) => (
                  <label
                    key={template.id}
                    className={`flex items-center p-4 rounded-lg border cursor-pointer transition-colors duration-200 ${
                      selectedTemplate === template.id
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                    } ${creatingProject ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="template"
                      value={template.id}
                      checked={selectedTemplate === template.id}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="mr-4 text-orange-500"
                      disabled={creatingProject}
                    />
                    <div>
                      <div className="font-semibold text-white">{template.name}</div>
                      <div className="text-sm text-gray-400">{template.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {!user && (
              <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-orange-300 text-sm">
                  💡 <strong>Sign up</strong> to save your projects in the cloud and access them from any device!
                </p>
              </div>
            )}

            <div className={`flex gap-4 ${isMobile ? 'flex-col' : ''}`}>
              <button
                onClick={handleCreateProject}
                disabled={!projectName.trim() || creatingProject}
                className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:from-gray-700 disabled:to-gray-800 disabled:text-gray-500 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center"
              >
                {creatingProject ? (
                  <>
                    <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Project'
                )}
              </button>
              <button
                onClick={() => {
                  setShowNewProject(false);
                  setProjectName('');
                  setSelectedTemplate('blank');
                }}
                disabled={creatingProject}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 rounded-xl font-semibold transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modals */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showImportExport && <ProjectImportExport onClose={() => setShowImportExport(false)} />}
      {showAICreator && <AIAppCreator onClose={() => setShowAICreator(false)} />}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        mode={authMode}
      />
    </div>
  );
};

export default StartScreen;